import axios from 'axios';

import { useAuthStore } from '../store/authStore';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = useAuthStore.getState().refreshToken;

      if (refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/auth/refresh`, {
            refreshToken,
          });

          const { accessToken } = response.data.data;
          useAuthStore.getState().setAccessToken(accessToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, logout user
          useAuthStore.getState().logout();
          window.location.href = '/login';
        }
      } else {
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),

  logout: (refreshToken: string) =>
    api.post('/auth/logout', { refreshToken }),

  me: () => api.get('/auth/me'),
};

// Devices API
export const devicesApi = {
  list: (params?: Record<string, unknown>) =>
    api.get('/devices', { params }),

  get: (id: string) => api.get(`/devices/${id}`),

  create: <T extends object>(data: T) =>
    api.post('/devices', data),

  update: <T extends object>(id: string, data: T) =>
    api.put(`/devices/${id}`, data),

  getLastPosition: (id: string) =>
    api.get(`/devices/${id}/last-position`),

  getPositions: (id: string, params?: { from?: string; to?: string }) =>
    api.get(`/devices/${id}/positions`, { params }),

  markOverdue: (id: string) =>
    api.patch(`/devices/${id}/overdue`),

  markNotOverdue: (id: string) =>
    api.patch(`/devices/${id}/not-overdue`),

  markRepo: (id: string) =>
    api.patch(`/devices/${id}/repo`),

  markNotRepo: (id: string) =>
    api.patch(`/devices/${id}/not-repo`),

  markAggressive: (id: string) =>
    api.patch(`/devices/${id}/aggressive`),

  markNotAggressive: (id: string) =>
    api.patch(`/devices/${id}/not-aggressive`),

  getAssigned: () =>
    api.get('/devices/assigned'),
};

// Alerts API
export const alertsApi = {
  list: (params?: Record<string, unknown>) =>
    api.get('/alerts', { params }),

  get: (id: string) =>
    api.get(`/alerts/${id}`),

  getByDevice: (deviceId: string, params?: Record<string, unknown>) =>
    api.get(`/alerts/device/${deviceId}`, { params }),

  getUnreadCount: () =>
    api.get('/alerts/unread-count'),

  markRead: (id: string) =>
    api.patch(`/alerts/${id}/read`),

  markAllRead: (companyId?: string) =>
    api.post('/alerts/mark-all-read', {}, { params: companyId ? { companyId } : {} }),

  updateStatus: (id: string, status: 'NEW' | 'READ' | 'ACKNOWLEDGED' | 'RESOLVED') =>
    api.patch(`/alerts/${id}/status`, { status }),

  acknowledge: (id: string) =>
    api.patch(`/alerts/${id}/status`, { status: 'ACKNOWLEDGED' }),

  resolve: (id: string) =>
    api.patch(`/alerts/${id}/status`, { status: 'RESOLVED' }),
};

// Users API
export const usersApi = {
  list: (params?: Record<string, unknown>) =>
    api.get('/users', { params }),

  get: (id: string) => api.get(`/users/${id}`),

  create: <T extends object>(data: T) =>
    api.post('/users', data),

  update: <T extends object>(id: string, data: T) =>
    api.put(`/users/${id}`, data),

  delete: (id: string) =>
    api.delete(`/users/${id}`),

  assignCompanies: (id: string, companyIds: string[]) =>
    api.post(`/users/${id}/assign-companies`, { companyIds }),

  assignDevices: (id: string, deviceIds: string[]) =>
    api.post(`/users/${id}/assign-devices`, { deviceIds }),
};

// Companies API
export const companiesApi = {
  list: (params?: Record<string, unknown>) =>
    api.get('/companies', { params }),

  get: (id: string) => api.get(`/companies/${id}`),

  create: <T extends object>(data: T) =>
    api.post('/companies', data),

  update: <T extends object>(id: string, data: T) =>
    api.put(`/companies/${id}`, data),

  delete: (id: string) =>
    api.delete(`/companies/${id}`),
};

// OAuth API Clients (integraciones tipo GyleERP) — admin only
export const oauthClientsApi = {
  list: (params?: Record<string, unknown>) =>
    api.get('/oauth/clients', { params }),

  get: (id: string) => api.get(`/oauth/clients/${id}`),

  create: <T extends object>(data: T) =>
    api.post('/oauth/clients', data),

  update: <T extends object>(id: string, data: T) =>
    api.put(`/oauth/clients/${id}`, data),

  regenerateSecret: (id: string) =>
    api.post(`/oauth/clients/${id}/regenerate-secret`),

  delete: (id: string) =>
    api.delete(`/oauth/clients/${id}`),

  listScopes: () => api.get('/oauth/scopes'),
};

// Clients API
export const clientsApi = {
  list: (params?: Record<string, unknown>) =>
    api.get('/clients', { params }),

  get: (id: string) => api.get(`/clients/${id}`),

  create: <T extends object>(data: T) =>
    api.post('/clients', data),

  update: <T extends object>(id: string, data: T) =>
    api.put(`/clients/${id}`, data),

  delete: (id: string) =>
    api.delete(`/clients/${id}`),

  getRepoData: (clientId: string) =>
    api.get(`/clients/${clientId}/repo-data`),

  createRepoData: (clientId: string, data: { photos?: string[]; phoneNumbers?: string[]; additionalInfo?: Record<string, unknown> }) =>
    api.post(`/clients/${clientId}/repo-data`, data),

  updateRepoData: (clientId: string, data: { photos?: string[]; phoneNumbers?: string[]; additionalInfo?: Record<string, unknown> }) =>
    api.put(`/clients/${clientId}/repo-data`, data),
};

// Geofences API
export const geofencesApi = {
  list: (params?: Record<string, unknown>) =>
    api.get('/geofences', { params }),

  get: (id: string) => api.get(`/geofences/${id}`),

  create: <T extends object>(data: T) =>
    api.post('/geofences', data),

  update: <T extends object>(id: string, data: T) =>
    api.put(`/geofences/${id}`, data),

  delete: (id: string) =>
    api.delete(`/geofences/${id}`),

  assignDevices: (id: string, deviceIds: string[]) =>
    api.post(`/geofences/${id}/devices`, { deviceIds }),

  removeDevices: (id: string, deviceIds: string[]) =>
    api.delete(`/geofences/${id}/devices`, { data: { deviceIds } }),
};

// Positions API
export const positionsApi = {
  getLastPositions: () =>
    api.get('/positions/last'),

  getDeviceLastPosition: (deviceId: string) =>
    api.get(`/positions/device/${deviceId}/last`),

  getDevicePositions: (deviceId: string, params?: Record<string, unknown>) =>
    api.get(`/positions/device/${deviceId}`, { params }),

  getHistory: (deviceId: string, params?: { from?: string; to?: string }) =>
    api.get(`/positions/history/${deviceId}`, { params }),

  getTrack: (deviceId: string, params?: { from?: string; to?: string }) =>
    api.get(`/positions/track/${deviceId}`, { params }),
};

// Reports API
export const reportsApi = {
  getDashboard: (params?: Record<string, unknown>) =>
    api.get('/reports/dashboard', { params }),

  getOfflineDevices: (params?: Record<string, unknown>) =>
    api.get('/reports/offline-devices', { params }),

  getOverdueDevices: (params?: Record<string, unknown>) =>
    api.get('/reports/overdue-devices', { params }),

  getRepoDevices: (params?: Record<string, unknown>) =>
    api.get('/reports/repo-devices', { params }),

  getGeofenceViolations: (params?: Record<string, unknown>) =>
    api.get('/reports/geofence-violations', { params }),

  getDeviceSummary: (deviceId: string, params?: Record<string, unknown>) =>
    api.get(`/reports/device-summary/${deviceId}`, { params }),
};
